import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import ProgressBar from "@/components/base/ProgressBar";
import { useGhlOverview } from "@/hooks/useGhlOverview";
import { useCourseProgress } from "@/hooks/useCourseProgress";
import { computeRouteModules, computeRouteStats, computeCoursesProgressAvg } from "@/lib/route";
import { fetchCurrentProfile, uploadAvatar } from "@/services/auth";
import { fetchMyQuizResult } from "@/services/quiz";
import { supabase } from "@/lib/supabaseClient";
import { fetchPipelineSummary, type PipelineSummary } from "@/services/pipeline";
import { pipelineStages } from "@/mocks/prospectos";

const menuItems: { icon: string; label: string; sub: string; to: string | null }[] = [
  { icon: "ri-user-settings-line", label: "Editar perfil", sub: "Nombre, foto, contraseña", to: null },
  { icon: "ri-shield-check-line", label: "Privacidad y seguridad", sub: "Tus datos están protegidos", to: null },
  { icon: "ri-whatsapp-line", label: "Mi agente de WhatsApp", sub: "Enciende o apaga el agente por contacto", to: "/app/mi-whatsapp" },
  { icon: "ri-notification-4-line", label: "Notificaciones", sub: "Avisos por WhatsApp", to: "/app/notificaciones" },
  { icon: "ri-palette-line", label: "Apariencia", sub: "Tema claro u oscuro", to: "/app/apariencia" },
  { icon: "ri-customer-service-line", label: "Soporte", sub: "Ayuda y preguntas frecuentes", to: null },
];

export default function Perfil() {
  const navigate = useNavigate();
  // Hábitos/diario/coach siguen viviendo en GHL (custom fields del contacto).
  const { data: ghl, noContact } = useGhlOverview();
  // Progreso de cursos/ruta ahora vive en Supabase (course_progress).
  const { progressByCourseId, loading: progressLoading } = useCourseProgress();
  const [fullName, setFullName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [profileLabel, setProfileLabel] = useState("Completa tu test");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pipe, setPipe] = useState<PipelineSummary | null>(null);

  useEffect(() => {
    let active = true;
    fetchPipelineSummary().then((r) => { if (active) setPipe(r); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    fetchCurrentProfile().then((profile) => {
      if (!active) return;
      if (profile?.fullName) setFullName(profile.fullName);
      if (profile?.avatarUrl) setAvatarUrl(profile.avatarUrl);
    });
    supabase.auth.getUser().then(({ data }) => {
      if (active && data.user?.email) setEmail(data.user.email);
    });
    fetchMyQuizResult()
      .then((result) => {
        if (active && result) setProfileLabel(result.profile.label);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    setAvatarError(null);
    try {
      const url = await uploadAvatar(file);
      setAvatarUrl(url);
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : "No se pudo subir la foto.");
    } finally {
      setAvatarUploading(false);
      e.target.value = "";
    }
  };

  // Misma lógica de Mi Ruta, para que el % de "Mi Ruta" en esta pantalla
  // coincida siempre con el de la pantalla de Mi Ruta.
  const modules = useMemo(() => computeRouteModules(progressByCourseId), [progressByCourseId]);
  const { routeProgress } = useMemo(() => computeRouteStats(modules), [modules]);

  // Racha de hábitos: dato real de GHL cuando hay contacto vinculado; si no,
  // se mantiene el valor de muestra.
  const streakDays = !noContact && ghl?.overview.habitsStreak ? Number(ghl.overview.habitsStreak) || 0 : 0;
  // % de cursos: promedio real desde Supabase (course_progress) una vez cargado.
  const coursesProgress = !progressLoading ? computeCoursesProgressAvg(progressByCourseId) ?? 0 : 0;

  return (
    <AppShell headerVariant="onLight">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Avatar card */}
        <div className="bg-background-950 text-white rounded-2xl p-6 md:p-8 relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-40"
            style={{ background: "radial-gradient(500px 200px at 90% 10%, oklch(var(--accent-700) / 0.55), transparent 60%)" }}></div>
          <div className="relative flex flex-col md:flex-row items-center md:items-start gap-5">
            <div className="relative">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={`Foto de perfil de ${fullName ?? "usuario"}`}
                  className="w-24 h-24 rounded-full object-cover object-top border-4 border-primary-500"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-primary-500/15 border-4 border-primary-500 flex items-center justify-center">
                  <i className="ri-user-3-line text-3xl text-primary-600"></i>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
              <button
                type="button"
                onClick={handleAvatarClick}
                disabled={avatarUploading}
                className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-primary-500 flex items-center justify-center cursor-pointer hover:bg-primary-600 disabled:opacity-60"
              >
                {avatarUploading ? (
                  <i className="ri-loader-4-line animate-spin text-background-950 text-xs"></i>
                ) : (
                  <i className="ri-camera-fill text-background-950 text-xs"></i>
                )}
              </button>
            </div>
            <div className="text-center md:text-left flex-1">
              <h1 className="font-heading font-extrabold text-2xl">{fullName ?? "Tu perfil"}</h1>
              <p className="text-foreground-300 text-sm">{email ?? ""}</p>
              <div className="mt-2 flex flex-wrap justify-center md:justify-start gap-2">
                <span className="inline-flex items-center gap-1 text-[11px] bg-accent-500/20 text-accent-200 px-2.5 py-1 rounded-full">
                  <i className="ri-brain-line"></i> {profileLabel}
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] bg-primary-500/20 text-primary-200 px-2.5 py-1 rounded-full">
                  🔥 {streakDays} días de racha
                </span>
              </div>
            </div>
            <button
              onClick={() => navigate("/app/resultado")}
              className="inline-flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:bg-white/10 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-sparkling-2-fill text-primary-300"></i> Ver mi resultado
            </button>
          </div>
          {avatarError ? <p className="relative mt-2 text-xs text-red-300 text-center md:text-left">{avatarError}</p> : null}
        </div>

        {/* Progress summary */}
        <div className="mt-5 grid grid-cols-3 gap-3">
          <div className="bg-background-100 border border-background-200 rounded-2xl p-4 text-center">
            <div className="font-heading font-extrabold text-2xl text-primary-600">{routeProgress}%</div>
            <div className="text-xs text-foreground-500 mt-1">Mi Ruta</div>
            <div className="mt-2"><ProgressBar value={routeProgress} color="primary" /></div>
          </div>
          <div className="bg-background-100 border border-background-200 rounded-2xl p-4 text-center">
            <div className="font-heading font-extrabold text-2xl text-accent-600">{coursesProgress}%</div>
            <div className="text-xs text-foreground-500 mt-1">Cursos</div>
            <div className="mt-2"><ProgressBar value={coursesProgress} color="accent" /></div>
          </div>
          <div className="bg-background-100 border border-background-200 rounded-2xl p-4 text-center">
            <div className="font-heading font-extrabold text-2xl text-amber-600">0%</div>
            <div className="text-xs text-foreground-500 mt-1">Masterclass</div>
            <div className="mt-2"><ProgressBar value={0} color="warning" /></div>
          </div>
        </div>

        {/* Mi pipeline */}
        {pipe && pipe.total > 0 ? (
          <div className="mt-5 bg-background-100 border border-background-200 rounded-2xl p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading font-bold text-foreground-950">Mi pipeline de prospectos</div>
                <div className="text-xs text-foreground-500">{pipe.total} prospectos en total</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => navigate("/app/contactos")} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-primary-500 text-white cursor-pointer whitespace-nowrap">Ver pipeline</button>
                <button onClick={() => navigate("/app/mi-whatsapp")} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-background-300 text-foreground-700 cursor-pointer whitespace-nowrap">Mi WhatsApp</button>
              </div>
            </div>
            {pipe.needsHuman > 0 ? (
              <button onClick={() => navigate("/app/contactos")} className="mt-3 w-full text-left text-xs bg-amber-500/10 border border-amber-500/30 text-amber-800 rounded-xl px-3 py-2 cursor-pointer">
                <i className="ri-hand-heart-line mr-1"></i> {pipe.needsHuman} {pipe.needsHuman === 1 ? "contacto necesita" : "contactos necesitan"} tu intervención
              </button>
            ) : null}
            <div className="mt-4 grid grid-cols-4 sm:grid-cols-7 gap-2">
              {pipelineStages.map((st) => (
                <div key={st} className="text-center rounded-xl bg-background-50 border border-background-200 py-2">
                  <div className="font-heading font-extrabold text-lg text-primary-600">{pipe.stages[st] ?? 0}</div>
                  <div className="text-[10px] text-foreground-500 leading-tight">{st}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Menu */}
        <div className="mt-6 bg-background-100 border border-background-200 rounded-2xl overflow-hidden divide-y divide-background-200">
          {menuItems.map((item) => (
            <button
              key={item.label}
              onClick={() => { if (item.to) navigate(item.to); }}
              className="w-full flex items-center gap-4 px-5 py-4 hover:bg-background-50 cursor-pointer text-left"
            >
              <span className="w-10 h-10 flex-shrink-0 rounded-xl bg-primary-500/10 flex items-center justify-center">
                <i className={`${item.icon} text-primary-600 text-lg`}></i>
              </span>
              <div className="flex-1">
                <div className="font-semibold text-sm text-foreground-950">{item.label}</div>
                <div className="text-xs text-foreground-500">{item.sub}</div>
              </div>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xl"></i>
            </button>
          ))}
        </div>

        {/* Logout */}
        <button
          onClick={() => navigate("/")}
          className="mt-4 w-full py-3.5 rounded-2xl bg-background-100 border border-background-200 text-accent-700 font-heading font-semibold inline-flex items-center justify-center gap-2 hover:bg-accent-500/5 hover:border-accent-400 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-logout-box-r-line"></i> Cerrar sesión
        </button>

        <div className="mt-8 text-center text-xs text-foreground-400">
          Vívelo AI · v2.1.0 · <span className="text-primary-500">No solo existas. Vívelo.</span>
        </div>
      </div>
    </AppShell>
  );
}
